<template>
    <Teleport to="body">
        <Transition name="modal">
            <div
                v-if="isOpen"
                role="dialog"
                aria-modal="true"
                aria-labelledby="ClipCommentsSheetTitle"
                class="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4"
                @keydown.esc="$emit('close')"
            >
                <!-- Backdrop -->
                <div class="absolute inset-0 bg-black/70 backdrop-blur-sm" @click="$emit('close')" />

                <div
                    ref="modalRef"
                    class="overflow-hidden relative w-full max-w-lg bg-zinc-950 rounded-t-3xl sm:rounded-2xl border-t border-white/10 sm:border border-white/10 shadow-2xl max-h-[90dvh] flex flex-col"
                    :style="modalStyle"
                    @touchstart="handleTouchStart"
                    @touchmove="handleTouchMove"
                    @touchend="handleTouchEnd"
                >
                    <!-- Mobile Pull Handle -->
                    <div class="sm:hidden flex justify-center pt-2 pb-1 cursor-grab active:cursor-grabbing">
                        <div class="w-10 h-1 rounded-full bg-zinc-700" />
                    </div>

                    <!-- Header -->
                    <div class="px-5 pt-4 pb-3 border-b border-white/10 flex items-center gap-2.5">
                        <h2 id="ClipCommentsSheetTitle" class="text-xl font-bold text-white flex-1">{{ titleText }}</h2>
                        <button
                            ref="closeButtonRef"
                            type="button"
                            @click="$emit('close')"
                            class="w-9 h-9 rounded-full hover:bg-white/5 flex items-center justify-center transition-colors shrink-0"
                            aria-label="Close"
                        >
                            <X class="w-4 h-4 text-white/70" />
                        </button>
                    </div>

                    <!-- Empty state: only once the first page has actually come back -->
                    <div
                        v-if="comments.length === 0 && !isLoading && hasLoadedOnce"
                        class="flex-1 flex items-center justify-center px-5 py-12"
                    >
                        <div class="text-center max-w-xs">
                            <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-500/10 border border-blue-500/20 mb-4">
                                <MessageCircle class="w-8 h-8 text-blue-400" />
                            </div>
                            <h3 class="text-white font-semibold text-base mb-2">No comments yet</h3>
                            <p class="text-white/60 text-sm leading-relaxed">
                                Be the first to say something about this clip.
                            </p>
                        </div>
                    </div>

                    <!-- Comment list: rendered in the order the endpoint returns it (newest first),
                         never re-sorted client-side. -->
                    <div
                        v-else
                        ref="scrollContainer"
                        class="flex-1 overflow-y-auto px-2 py-2 sm:max-h-[500px] max-h-[60dvh]"
                        @scroll="handleScroll"
                    >
                        <div v-for="comment in comments" :key="comment.id" class="px-3 py-2.5">
                            <div class="flex items-start gap-3">
                                <div class="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm shrink-0">
                                    {{ getInitial(comment.author) }}
                                </div>
                                <div class="flex-1 min-w-0">
                                    <div class="flex items-baseline gap-2">
                                        <p class="text-white font-semibold text-sm truncate">{{ comment.author }}</p>
                                        <span class="text-white/30 text-[11px] shrink-0">
                                            {{ getRelativeTime(comment.timestamp) }}
                                        </span>
                                    </div>
                                    <p class="text-white/70 text-sm leading-relaxed break-words whitespace-pre-line">
                                        {{ comment.body }}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <!-- Loading skeletons -->
                        <div v-if="isLoading" class="space-y-0">
                            <div v-for="i in 3" :key="`skeleton-${i}`" class="animate-pulse px-3 py-2.5">
                                <div class="flex items-start gap-3">
                                    <div class="w-8 h-8 rounded-full bg-white/10 shrink-0" />
                                    <div class="flex-1 space-y-2">
                                        <div class="h-3.5 bg-white/10 rounded w-24" />
                                        <div class="h-3 bg-white/10 rounded w-full" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-if="!hasMore && comments.length > 0" class="text-center py-4">
                            <p class="text-white/40 text-xs">That's everything!</p>
                        </div>
                    </div>

                    <!-- Compose box -->
                    <div class="px-5 py-4 border-t border-white/10 bg-zinc-950">
                        <template v-if="isUserLoggedIn">
                            <textarea
                                ref="textareaRef"
                                v-model="commentText"
                                placeholder="Add a comment…"
                                aria-label="Add a comment"
                                rows="2"
                                maxlength="500"
                                class="w-full px-4 py-3 rounded-xl bg-zinc-900 border border-white/10 text-white text-sm placeholder:text-white/30 focus:border-blue-500 focus:outline-none transition-colors resize-none"
                            />
                            <div class="flex items-center justify-between gap-3 mt-1.5">
                                <span class="text-[11px]" :class="commentText.length > 0 ? 'text-white/40' : 'text-transparent'">
                                    {{ commentText.length }}/500
                                </span>
                                <span v-if="submitError" class="text-[11px] text-red-400 flex-1 text-right truncate">
                                    {{ submitError }}
                                </span>
                                <button
                                    @click="submitComment"
                                    :disabled="!canSubmit"
                                    :class="[
                                        'px-4 py-2 font-semibold rounded-lg text-sm transition-all flex items-center gap-2 shrink-0',
                                        canSubmit
                                            ? 'bg-blue-600 hover:bg-blue-700 text-white active:scale-95'
                                            : 'bg-zinc-800 text-white/30 cursor-not-allowed'
                                    ]"
                                >
                                    <Send class="w-4 h-4" />
                                    Post
                                </button>
                            </div>
                        </template>

                        <!-- Guest: the list stays readable, only posting is gated. -->
                        <button
                            v-else
                            @click="goToJoinPage"
                            class="w-full py-2.5 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-semibold text-sm rounded-lg transition-all active:scale-95"
                        >
                            Join to comment
                        </button>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import axios from 'axios'
import { MessageCircle, Send, X } from 'lucide-vue-next'
import { getRelativeTime } from '@/Helpers/relativeTime'
import { useClipAnalytics } from '@/composables/useClipAnalytics'

const props = defineProps({
    isOpen: {
        type: Boolean,
        required: true,
    },
    clip: {
        type: Object,
        default: null,
    },
    routePrefix: {
        type: String,
        required: true,
    },
    isUserLoggedIn: {
        type: Boolean,
        required: true,
    },
})

const emit = defineEmits(['close', 'commentAdded'])

const { trackJoinCtaClicked, trackClipCommented } = useClipAnalytics({ routePrefix: props.routePrefix })

const comments = ref([])
const page = ref(1)
const total = ref(0)
const hasMore = ref(false)
const hasLoadedOnce = ref(false)
const isLoading = ref(false)
const scrollContainer = ref(null)

const commentText = ref('')
const textareaRef = ref(null)
const closeButtonRef = ref(null)
// Focus goes in on open — to Close, not the textarea, so a phone keyboard doesn't pop up
// unasked — and back to whatever opened the sheet on close.
let returnFocusEl = null
const isSubmitting = ref(false)
const submitError = ref('')

const titleText = computed(() => (total.value === 0 ? 'Comments' : `Comments (${total.value})`))
const canSubmit = computed(() => !!commentText.value.trim() && !isSubmitting.value)

const getInitial = (author) => (author ?? '?').charAt(0).toUpperCase()

const fetchPage = async (requestedPage) => {
    if (!props.clip?.slug || isLoading.value) return

    isLoading.value = true

    try {
        const response = await axios.get(
            route(props.routePrefix + '.verticalClips.comments.index', props.clip.slug),
            { params: { page: requestedPage } }
        )

        const data = response.data

        // Page 1 replaces, later pages append — the endpoint already hands them back
        // newest-first, so appending keeps that order.
        comments.value = requestedPage === 1
            ? [...(data.comments ?? [])]
            : [...comments.value, ...(data.comments ?? [])]

        page.value = data.page ?? requestedPage
        total.value = data.total ?? comments.value.length
        hasMore.value = !!data.hasMore
        hasLoadedOnce.value = true
    } catch (error) {
        console.error('Failed to load clip comments:', error)
        hasLoadedOnce.value = true
    } finally {
        isLoading.value = false
    }
}

// Each open is a fresh read: the active clip may have changed since the last one.
watch(() => props.isOpen, (isOpen) => {
    if (!isOpen) {
        if (returnFocusEl?.isConnected) returnFocusEl.focus()
        returnFocusEl = null

        return
    }

    returnFocusEl = document.activeElement
    nextTick(() => closeButtonRef.value?.focus())

    comments.value = []
    page.value = 1
    total.value = 0
    hasMore.value = false
    hasLoadedOnce.value = false
    commentText.value = ''
    submitError.value = ''

    if (scrollContainer.value) scrollContainer.value.scrollTop = 0

    fetchPage(1)
})

const handleScroll = (e) => {
    if (isDragging.value) return

    const container = e.target
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight

    if (distanceFromBottom < 200 && hasMore.value && !isLoading.value) {
        fetchPage(page.value + 1)
    }
}

const submitComment = async () => {
    if (!canSubmit.value || !props.clip?.slug) return

    isSubmitting.value = true
    submitError.value = ''

    try {
        const response = await axios.post(
            route(props.routePrefix + '.verticalClips.comments.store', props.clip.slug),
            { body: commentText.value.trim() }
        )

        // Prepended locally rather than refetched — the list is newest-first, so the
        // comment just posted belongs at the top.
        comments.value = [response.data.comment, ...comments.value]
        total.value += 1
        commentText.value = ''

        emit('commentAdded')
        trackClipCommented(props.clip)

        nextTick(() => {
            if (scrollContainer.value) scrollContainer.value.scrollTop = 0
        })
    } catch (error) {
        submitError.value = error?.response?.data?.error ?? 'Could not post that comment.'
    } finally {
        isSubmitting.value = false
    }
}

const goToJoinPage = () => {
    const joinUrl = route('www.join.signUp', {
        custom6: 'clipsCommentIcon',
        custom7: props.clip?.slug,
    })

    trackJoinCtaClicked('clipsCommentIcon', props.clip, joinUrl)

    window.location.href = joinUrl
}

// Touch/swipe-to-dismiss, same shape as TopSponsorsSheet
const modalRef = ref(null)
const touchStartY = ref(0)
const touchCurrentY = ref(0)
const isDragging = ref(false)

const modalStyle = computed(() => {
    if (!isDragging.value) return {}

    const deltaY = touchCurrentY.value - touchStartY.value
    if (deltaY < 0) return {}

    return { transform: `translateY(${deltaY}px)`, transition: 'none' }
})

const handleTouchStart = (e) => {
    if (!modalRef.value) return

    const touch = e.touches[0]
    const rect = modalRef.value.getBoundingClientRect()

    // Only the handle area drags the sheet; below it the list scrolls normally.
    if (touch.clientY - rect.top > 60) return

    touchStartY.value = touch.clientY
    touchCurrentY.value = touch.clientY
    isDragging.value = true
}

const handleTouchMove = (e) => {
    if (!isDragging.value) return

    touchCurrentY.value = e.touches[0].clientY

    if (touchCurrentY.value - touchStartY.value > 0) e.preventDefault()
}

const handleTouchEnd = () => {
    if (!isDragging.value) return

    if (touchCurrentY.value - touchStartY.value > 100) emit('close')

    isDragging.value = false
    touchStartY.value = 0
    touchCurrentY.value = 0
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}
</style>
